import React from "react";
import { Euro, Flame } from "lucide-react";
import { Bar, CartesianGrid, Cell, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useEaData } from "@/components/energy-analytics/analytics/useEaData";
import { useGasUnit } from "@/components/energy-analytics/analytics/EaContext";
import { axisFmt } from "@/components/energy-analytics/analytics/EaConsumptionCharts";
import { EaChartCard, EaGasUnitToggle, EaKpiCard, EaNote, EaPageHeader } from "@/components/energy-analytics/analytics/EaUi";
import { EaExportButton, EaPointPicker, EaTable, TOT, useSelectedCodes } from "@/components/energy-analytics/analytics/EaControls";
import { EaAvgCostCard } from "@/components/energy-analytics/analytics/EaAvgCostCard";
import { COLORS, MONTHS_IT, fmtEur, fmtEurCompact, fmtQty, fmtSignedPct, gasLabel, pointSub } from "@/components/energy-analytics/analytics/eaFormat";
import { PageState } from "./EaPowerOverview";

const axis = { tick: { fontSize: 11, fill: "#64748B" }, axisLine: false, tickLine: false };
export const smc = (v) => fmtQty(v, 0, "Smc");

function TopCard({ icon: Icon, label, row, value, testid }) {
  return (
    <div className="card-elevated p-4 flex items-center gap-4" data-testid={testid} data-pdf="kpi">
      <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0"><Icon size={18} /></div>
      <div className="min-w-0">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500" data-pdf-label>{label}</div>
        <div className="font-semibold text-slate-900 truncate" data-pdf-value>{row ? gasLabel(row) : "—"}</div>
        <div className="text-xs text-slate-500 font-mono-nums" data-pdf-sub>{row ? value(row) : ""}</div>
      </div>
    </div>
  );
}

function MonthlyChart({ monthly }) {
  const g = useGasUnit();
  const data = monthly.map((m) => ({ ...m, label: MONTHS_IT[m.month - 1], cons: g.q(m.consumption_actual ?? m.consumption_forecast), fc: m.status === "FORECAST" }));
  return (
    <div className="h-80" data-testid="ea-gas-ov-chart">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
          <XAxis dataKey="label" {...axis} />
          <YAxis yAxisId="c" {...axis} width={80} tickFormatter={axisFmt} label={{ value: g.u, angle: -90, position: "insideLeft", fontSize: 11, fill: "#64748B" }} />
          <YAxis yAxisId="e" orientation="right" {...axis} width={72} tickFormatter={fmtEurCompact} />
          <Tooltip formatter={(v, n) => [n === "Spesa fatturata" ? fmtEur(v) : fmtQty(v, g.d, g.u), n]} labelFormatter={(l, p) => `${l}${p?.[0]?.payload?.fc ? " · previsionale" : ""}`} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar yAxisId="c" dataKey="cons" name="Consumo" fill={COLORS.gas} radius={[3, 3, 0, 0]}>
            {data.map((r) => <Cell key={r.month} fillOpacity={r.fc ? 0.3 : 1} />)}
          </Bar>
          <Line yAxisId="e" dataKey="spend" name="Spesa fatturata" stroke="#071B4D" strokeWidth={2} dot={{ r: 3 }} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function EaGasOverview() {
  const codes = useSelectedCodes("GAS");
  const { data: d, loading, error } = useEaData("/ea/gas/overview", { codes });
  const g = useGasUnit();
  const k = d?.kpis;
  const ru = k?.raw_material_unit || "";
  const rows = (d?.rows || []).map((r) => ({ ...r, consumption: g.q(r.consumption), contractual: g.q(r.contractual), avg_cost: g.p(r.avg_cost), raw_material_price: g.p(r.raw_material_price, ru) }));
  const qf = (v) => fmtQty(v, g.d);
  const cols = [
    { key: "identifier", label: "Utenza", align: "left", render: (r) => <div><div className="font-semibold text-slate-900">{gasLabel(r)}</div><div className="text-xs text-slate-500">{pointSub(r)}</div></div> },
    { key: "consumption", label: `Consumo (${g.u})`, fmt: qf, total: "sum" },
    { key: "consumption_share_pct", label: "% consumo", fmt: (v) => fmtQty(v, 1, "%"), total: "sum" },
    { key: "contractual", label: `Contrattuale (${g.u})`, fmt: qf, total: "sum" },
    { key: "spend_ytd", label: "Spesa fatturata", fmt: (v) => fmtEur(v), total: "sum" },
    { key: "spend_share_pct", label: "% spesa", fmt: (v) => fmtQty(v, 1, "%"), total: "sum" },
    { key: "avg_cost", label: `Costo medio ${g.pu("c€/Smc")}`, fmt: (v) => fmtQty(v, 2), total: () => g.p(k?.avg_cost) },
    { key: "raw_material_price", label: `Materia prima ${g.pu(ru)}`, fmt: (v) => fmtQty(v, 2), total: TOT.wavg("raw_material_price", "consumption") },
    { key: "forecast", label: "Forecast anno", fmt: (v) => fmtEur(v), total: "sum" },
  ];
  const header = <EaPageHeader title="Panoramica Gas" subtitle={`Tutte le utenze gas selezionate${d?.year ? ` · anno ${d.year}` : ""}.`} units={<EaGasUnitToggle />} right={<EaPointPicker commodity="GAS" />} />;
  const contrDelta = k?.contractual ? (k.consumption_actual / k.contractual - 1) * 100 : null;
  return (
    <PageState header={header} loading={loading} error={error} data={d} testid="ea-gas-overview">
      {k && <>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <EaKpiCard label="Consumo (consuntivo)" accent={COLORS.gas} value={g.f(k.consumption_actual)} testid="ea-gas-kpi-consumption"
            sub={`${k.months_actual} mesi a consuntivo · anno ${g.f(k.consumption_year)}`} />
          <EaKpiCard label="Spesa fatturata" value={fmtEur(k.spend_ytd)} testid="ea-gas-kpi-spend"
            sub={`${k.months_invoiced} su 12${k.months_partial?.length ? ` + ${k.months_partial.length} parziali` : ""}${k.last_invoiced_month ? ` · fino a ${MONTHS_IT[k.last_invoiced_month - 1]}` : ""}`} />
          <EaAvgCostCard block={k} label="Costo medio totale" unit={g.pu("c€/Smc")} conv={(v) => g.p(v)} accent={COLORS.gas} testid="ea-gas-kpi-avg" />
          <EaKpiCard label="Prezzo medio materia prima" value={fmtQty(g.p(k.raw_material_price, ru), 2, g.pu(ru))} testid="ea-gas-kpi-raw" sub="Ponderato sui consumi dei mesi a consuntivo" />
          <EaKpiCard label="Consumo contrattuale" value={g.f(k.contractual)} testid="ea-gas-kpi-contractual" sub={`Effettivo ${fmtSignedPct(contrDelta)} rispetto al contratto (mesi a consuntivo)`} />
          <EaKpiCard label="Utenze gas" value={k.points} testid="ea-gas-kpi-points" sub={`${k.pdr} indirette (PDR + REMI) · ${k.snam_direct} dirette rete Snam (solo REMI)`} />
          <EaKpiCard label="PCS medio" value={fmtQty(k.pcs_avg, 0, "kJ/Smc")} testid="ea-gas-kpi-pcs" sub="Potere calorifico superiore dei mesi a consuntivo" />
          <EaKpiCard label="Forecast anno" value={fmtEur(k.forecast)} testid="ea-gas-kpi-forecast" sub={`Budget ${fmtEur(k.budget)}`} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TopCard icon={Flame} label="Maggior consumo" row={d.top.consumption} value={(r) => g.f(r.consumption)} testid="ea-gas-top-consumption" />
          <TopCard icon={Euro} label="Maggior costo" row={d.top.cost} value={(r) => fmtEur(r.spend_ytd)} testid="ea-gas-top-cost" />
        </div>
        <EaChartCard title="Consumi e spesa mensili" subtitle="Barre piene = consuntivo, tenui = previsionale · linea = spesa fatturata" testid="ea-gas-ov-card">
          <MonthlyChart monthly={d.monthly} />
        </EaChartCard>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="font-display font-semibold text-slate-900">Dettaglio utenze</div>
          <EaExportButton filename={`gas_panoramica_${d.year}`} commodity="GAS" year={d.year} columns={cols.map(({ key, label }) => ({ key, label }))} rows={rows} testid="ea-gas-export" />
        </div>
        <EaTable columns={cols} rows={rows} testid="ea-gas-table" initialSort={{ key: "spend_ytd", dir: "desc" }} />
        <EaNote>Dati giornalieri Gas non disponibili: le analisi sono mensili.</EaNote>
      </>}
    </PageState>
  );
}
